import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useHotelDashboard } from '@/hooks/useHotelDashboard'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import { CalendarDays, Plug } from 'lucide-react'
import EventsCalendar from '@/components/hotel/EventsCalendar'

export default function Events() {
  const { hotelId, hotel } = useHotelDashboard()
  const queryClient = useQueryClient()
  const [form, setForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    event_type: 'other',
    expected_impact: 'medium',
    venue: '',
  })
  const [saving, setSaving] = useState(false)

  const { data: events = [], isLoading } = useQuery({
    queryKey: ['local_events', hotelId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('local_events')
        .select('id, name, start_date, end_date, event_type, expected_impact, venue')
        .eq('hotel_id', hotelId!)
        .order('start_date', { ascending: true })
      if (error) throw error
      return data
    },
    enabled: !!hotelId,
  })

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault()
    if (!hotelId || !form.name.trim() || !form.start_date) return
    setSaving(true)
    try {
      const { error } = await supabase.from('local_events').insert({
        hotel_id: hotelId,
        name: form.name.trim(),
        start_date: form.start_date,
        end_date: form.end_date || null,
        event_type: form.event_type,
        expected_impact: form.expected_impact,
        venue: form.venue.trim() || null,
        city: hotel?.city ?? null,
        source: 'manual',
      })
      if (error) throw error
      toast.success('Event added')
      setForm({
        name: '',
        start_date: '',
        end_date: '',
        event_type: 'other',
        expected_impact: 'medium',
        venue: '',
      })
      queryClient.invalidateQueries({ queryKey: ['local_events', hotelId] })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add event')
    } finally {
      setSaving(false)
    }
  }

  const cityLabel = [hotel?.city, hotel?.state].filter(Boolean).join(', ') || null

  return (
    <div className="min-h-full space-y-8 p-6 text-vesta-navy">
      <div>
        <h1 className="text-2xl font-semibold text-vesta-navy">Local events</h1>
        <p className="mt-1 text-vesta-navy/80">
          Concerts, conferences, and sports that may affect demand{cityLabel ? ` near ${cityLabel}` : ''}.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="border border-vesta-navy/10 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CalendarDays className="h-4 w-4 text-vesta-gold" />
              Calendar
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="h-48 animate-pulse rounded-lg bg-vesta-mist/50" />
            ) : (
              <EventsCalendar events={events} cityLabel={cityLabel} />
            )}
          </CardContent>
        </Card>

        <Card className="border border-vesta-navy/10 bg-white shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Add event</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <Label htmlFor="ev-name">Event name</Label>
                <Input
                  id="ev-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ev-start">Start date</Label>
                  <Input
                    id="ev-start"
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                    className="mt-1"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="ev-end">End date</Label>
                  <Input
                    id="ev-end"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Impact</Label>
                  <Select
                    value={form.expected_impact}
                    onValueChange={(v) => setForm((f) => ({ ...f, expected_impact: v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={form.event_type}
                    onValueChange={(v) => setForm((f) => ({ ...f, event_type: v }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="concert">Concert</SelectItem>
                      <SelectItem value="conference">Conference</SelectItem>
                      <SelectItem value="sports">Sports</SelectItem>
                      <SelectItem value="festival">Festival</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="ev-venue">Venue</Label>
                <Input
                  id="ev-venue"
                  value={form.venue}
                  onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-vesta-gold text-vesta-navy">
                Save event
              </Button>
            </form>

            <div className="mt-6 rounded-lg border border-vesta-navy/10 bg-vesta-mist/20 p-4 text-sm">
              <p className="flex items-center gap-2 font-medium text-vesta-navy">
                <Plug className="h-4 w-4" />
                Bulk import
              </p>
              <p className="mt-1 text-vesta-navy/75">Import events CSV on Integrations.</p>
              <Button asChild variant="outline" size="sm" className="mt-3">
                <Link to="/integrations">Integrations</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
